import type { DataTableCell, DataTableColumn, DataTableColumnGroup } from '@app/component/chart/DataTable';
import type { RevenueScope, RevenueValues, SegmentationRow, SegmentSituation, SituationDays, VatBasis } from '@app/model/bi/Bi';
import { occupiedOf, revenueOf } from '../Bi.utilities';

// The pure functions of the Segmentation table (no React): the columns and the cells of the column sets.

// The column sets of the table (grill decision 28). 'today' = Mix, RN, Rev, ADR; 'dN' = situation D-N; 'puN' = pickup N.
export type ColumnSet = 'today' | 'd1' | 'd3' | 'd7' | 'd14' | 'd30' | 'pu1' | 'pu3' | 'pu7' | 'pu14' | 'pu30';

export const daysOf = (set: ColumnSet): SituationDays => Number(set.replace(/\D/g, '')) as SituationDays;
// A situation that the row lacks: no night, so every revenue is 0.
const NOTHING: Omit<SegmentSituation, 'daysBefore'> = { roomNights: 0, occupiedRooms: 0, roomRevenue: 0, roomRevenueNet: 0, totalRevenue: 0, totalRevenueNet: 0 };
export const situation = (row: SegmentationRow, days: SituationDays) => row.situations.find(s => s.daysBefore === days) ?? NOTHING;

// Every figure of a row and of its segments, for the notes of the caption.
export const figuresOf = (row: SegmentationRow): RevenueValues[] => [...row.situations, ...row.segments.flatMap(figuresOf)];

export const columnsOf = (sets: ColumnSet[], first: string): { columns: DataTableColumn[], groups: DataTableColumnGroup[] } => {
	const columns: DataTableColumn[] = [{ id: 'name', label: first, sortable: false }];
	const groups: DataTableColumnGroup[] = [{ label: '', span: 1 }];
	sets.forEach(set => {
		if (set === 'today') {
			columns.push(
				{ id: 'mix', label: 'Mix', format: 'percent', decimals: 1, sortable: false },
				{ id: 'rn0', label: 'RN', format: 'number', sortable: false },
				{ id: 'rev0', label: 'Rev', format: 'money', sortable: false },
				{ id: 'adr0', label: 'ADR', format: 'money', sortable: false },
			);
			groups.push({ label: 'Today', span: 4 });
			return;
		}
		const pickup = set.startsWith('pu');
		columns.push(
			{ id: `${set}rn`, label: 'RN', format: 'number', signed: pickup, tone: pickup ? 'change' : undefined, sortable: false },
			{ id: `${set}rev`, label: 'Rev', format: 'money', signed: pickup, tone: pickup ? 'change' : undefined, sortable: false },
		);
		groups.push({ label: pickup ? `PU ${daysOf(set)}` : `Situation D-${daysOf(set)}`, span: 2 });
	});
	return { columns, groups };
};

// Rev and ADR are the revenue of the switches of the page (ROO2-236); a revenue with no value gives null ("–"), and so
// does a pickup with one. Room nights and the Mix do not change with the switches. ROO2-315 part c1: ADR = revenue ÷ the
// occupied rooms of the report rules (the room nights without rules).
export const cellsOf = (row: SegmentationRow, sets: ColumnSet[], totalNights: number, scope: RevenueScope, vat: VatBasis): Record<string, DataTableCell> => {
	const today = situation(row, 0);
	const todayRevenue = revenueOf(today, scope, vat);
	const cells: Record<string, DataTableCell> = { name: row.title };
	sets.forEach(set => {
		if (set === 'today') {
			cells.mix = totalNights === 0 ? null : today.roomNights / totalNights;
			cells.rn0 = today.roomNights;
			cells.rev0 = todayRevenue === null ? null : Math.round(todayRevenue);
			cells.adr0 = occupiedOf(today) === 0 || todayRevenue === null ? null : Math.round(todayRevenue / occupiedOf(today));
			return;
		}
		const then = situation(row, daysOf(set));
		const thenRevenue = revenueOf(then, scope, vat);
		const pickup = set.startsWith('pu');
		cells[`${set}rn`] = pickup ? today.roomNights - then.roomNights : then.roomNights;
		if (pickup) cells[`${set}rev`] = todayRevenue === null || thenRevenue === null ? null : Math.round(todayRevenue - thenRevenue);
		else cells[`${set}rev`] = thenRevenue === null ? null : Math.round(thenRevenue);
	});
	return cells;
};
